import { asyncResult } from '@vertex-protocol/utils';
import { ActionToast } from 'client/components/Toast/ActionToast/ActionToast';
import { DEFAULT_TOAST_TTL } from 'client/components/Toast/consts';
import { useSubaccountContext } from 'client/context/subaccount/SubaccountContext';
import { useExecuteUpdateLinkedSigner } from 'client/hooks/execute/useExecuteUpdateLinkedSigner';
import { useRunWithDelayOnCondition } from 'client/hooks/util/useRunWithDelayOnCondition';
import { useDialog } from 'client/modules/app/dialogs/hooks/useDialog';
import { useNotificationManagerContext } from 'client/modules/notifications/NotificationManagerContext';
import { useSuppressedOrphanedTpSlDigests } from 'client/modules/trading/hooks/useSuppressedOrphanedTpSlDigests';
import { BaseActionButtonState } from 'client/types/BaseActionButtonState';
import { createToastId } from 'client/utils/createToastId';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';

export function useSingleSignatureReapprovalDialog() {
  const { dispatchNotification } = useNotificationManagerContext();
  const { hide } = useDialog();
  // This execute will skip the engine API call if the derived wallet matches backend
  const executeUpdateLinkedSigner = useExecuteUpdateLinkedSigner();
  const { signingPreference } = useSubaccountContext();
  const { clearSuppressedDigests } = useSuppressedOrphanedTpSlDigests();
  const [rememberMe, setRememberMe] = useState(false);

  useRunWithDelayOnCondition({
    condition: executeUpdateLinkedSigner.isSuccess,
    fn: hide,
    // Adding a shorter delay since it's a high-usage dialog
    delay: 1000,
  });

  // Sync remember me state
  useEffect(() => {
    if (signingPreference.current?.type !== 'sign_once') {
      return;
    }
    setRememberMe(signingPreference.current?.rememberMe ?? false);
  }, [signingPreference]);

  const onSubmit = useCallback(async () => {
    const mutationResultPromise = executeUpdateLinkedSigner.mutateAsync({
      revoke: false,
    });

    dispatchNotification({
      type: 'action_error_handler',
      data: {
        errorNotificationTitle: 'Approve Failed',
        executionData: {
          serverExecutionResult: mutationResultPromise,
        },
      },
    });

    // In this case, we actually care about the returned data, so await the result
    const [linkedSigner] = await asyncResult(mutationResultPromise);
    if (linkedSigner) {
      signingPreference.update({
        type: 'sign_once',
        linkedSigner,
        rememberMe,
      });
      // Clear any previously suppressed orphaned TP/SL digests now that the
      // linked signer is refreshed and auto-cancellation can resume.
      clearSuppressedDigests();

      const toastId = createToastId('approve_1ct');
      toast.custom(
        (t) => (
          <ActionToast.Container visible={t.visible}>
            <ActionToast.TextHeader
              variant="success"
              onDismiss={() => toast.dismiss(t.id)}
            >
              1-Click Trading Approved
            </ActionToast.TextHeader>
            <ActionToast.Separator variant="success" ttl={DEFAULT_TOAST_TTL} />
            <ActionToast.Body variant="success">
              You can now trade without signing each transaction.
            </ActionToast.Body>
          </ActionToast.Container>
        ),
        { id: toastId, duration: DEFAULT_TOAST_TTL },
      );
    }
  }, [
    clearSuppressedDigests,
    dispatchNotification,
    executeUpdateLinkedSigner,
    rememberMe,
    signingPreference,
  ]);

  const buttonState: BaseActionButtonState = (() => {
    switch (executeUpdateLinkedSigner.status) {
      case 'success':
        return 'success';
      case 'pending':
        return 'loading';
      default:
        return 'idle';
    }
  })();

  return {
    rememberMe,
    setRememberMe,
    buttonState,
    onSubmit,
  };
}
